// module_roots/DashboardBusinessManager.js

import React, { useState, useEffect, useRef, Suspense, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as Icons from '@fortawesome/free-solid-svg-icons';
import "../styles/Dashboard.css";

// ==============================================
// IMPORT MODULE CONFIGURATIONS
// ==============================================
import { getModuleByRole, MODULE_CONFIGS, ROLE_TO_MODULE } from '../module_configs';

// ==============================================
// IMPORT SHARED COMPONENTS (Internal to Dashboard)
// ==============================================
import SidePanel from './SidePanel';
import MessageSystem from './MessageSystem';
import NotificationForm from './Notifications';
import NotificationCenter from './NotificationsCenter';
import ShopSwitcher from './ShopSwitcher';

// ==============================================
// SHARED TRANSLATOR
// ==============================================

class InstantTranslator {
  constructor() {
    this.cache = new Map();
    this.preloadedLanguages = new Set(['en', 'ur', 'ar', 'es']);
    this.translationQueue = [];
    this.isTranslating = false;
    this.loadFromCache();
  }

  loadFromCache() {
    try {
      const cached = localStorage.getItem('ourzest_translations');
      if (cached) {
        const parsed = JSON.parse(cached);
        Object.entries(parsed).forEach(([key, value]) => {
          this.cache.set(key, value);
        });
      }
    } catch (e) {
      console.log("No cache found");
    }
  }

  saveToCache() {
    const cacheObj = {};
    this.cache.forEach((value, key) => {
      cacheObj[key] = value;
    });
    localStorage.setItem('ourzest_translations', JSON.stringify(cacheObj));
  }

  async translateAPI(text, targetLang) {
    const apis = [
      async () => {
        try {
          const response = await fetch('https://translate.argosopentech.com/translate', {
            method: 'POST',
            body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
            headers: { 'Content-Type': 'application/json' }
          });
          if (response.ok) {
            const data = await response.json();
            return data.translatedText;
          }
        } catch (e) { }
        return null;
      },
      async () => {
        try {
          const response = await fetch(
            `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
          );
          if (response.ok) {
            const data = await response.json();
            return data.responseData.translatedText;
          }
        } catch (e) { }
        return null;
      },
      async () => {
        const dict = {
          'ur': {
            'dashboard': 'ڈیش بورڈ',
            'welcome': 'خوش آمدید',
            'searchPlaceholder': 'ماڈیولز، رپورٹس، یا مدد تلاش کریں...',
            'notifications': 'نوٹیفیکیشنز',
            'markAllRead': 'سب پڑھے ہوئے نشان لگائیں',
            'viewAll': 'سب دیکھیں',
            'myProfile': 'میرا پروفائل',
            'settings': 'ترتیبات',
            'helpSupport': 'مدد اور تعاون',
            'logout': 'لاگ آؤٹ',
            'modules': 'ماڈیولز',
            'systemOnline': 'سسٹم آن لائن',
            'quickHelp': 'فوری مدد',
            'todayTasks': 'آج کے کام',
            'performance': 'کارکردگی',
            'pendingActions': 'زیر التواء کارروائیاں',
            'activeUsers': 'فعال صارفین',
            'loading': 'لوڈ ہو رہا ہے...',
            'theme': 'تھیم کا رنگ',
            'language': 'زبان',
            'custom': 'اپنی مرضی کا رنگ',
            'switchShops': 'شاپس تبدیل کریں',
            'availableShops': 'دستیاب شاپس',
            'loadingShops': 'شاپس لوڈ ہو رہی ہیں...',
            'noShopsAvailable': 'کوئی شاپ دستیاب نہیں',
            'refreshShops': 'شاپس ریفریش کریں',
            'role': 'رول',
            'close': 'بند کریں',
            'default': 'ڈیفالٹ'
          },
          'ar': {
            'dashboard': 'لوحة التحكم',
            'welcome': 'مرحبا بعودتك',
            'searchPlaceholder': 'البحث عن الوحدات أو التقارير أو المساعدة...',
            'notifications': 'الإشعارات',
            'markAllRead': 'تحديد الكل كمقروء',
            'viewAll': 'عرض الكل',
            'myProfile': 'ملفي الشخصي',
            'settings': 'الإعدادات',
            'helpSupport': 'المساعدة والدعم',
            'logout': 'تسجيل الخروج',
            'modules': 'الوحدات',
            'systemOnline': 'النظام يعمل',
            'quickHelp': 'مساعدة سريعة',
            'todayTasks': 'مهام اليوم',
            'performance': 'الأداء',
            'pendingActions': 'الإجراءات المعلقة',
            'activeUsers': 'المستخدمين النشطين',
            'loading': 'جاري التحميل...',
            'theme': 'لون السمة',
            'language': 'اللغة',
            'custom': 'لون مخصص',
            'switchShops': 'تبديل المتاجر',
            'availableShops': 'المتاجر المتاحة',
            'loadingShops': 'جاري تحميل المتاجر...',
            'noShopsAvailable': 'لا توجد متاجر متاحة',
            'refreshShops': 'تحديث المتاجر',
            'role': 'الدور',
            'close': 'إغلاق',
            'default': 'افتراضي'
          },
          'es': {
            'dashboard': 'Panel de Control',
            'welcome': 'Bienvenido',
            'searchPlaceholder': 'Buscar módulos, informes o ayuda...',
            'notifications': 'Notificaciones',
            'markAllRead': 'Marcar todo como leído',
            'viewAll': 'Ver todo',
            'myProfile': 'Mi Perfil',
            'settings': 'Configuración',
            'helpSupport': 'Ayuda y Soporte',
            'logout': 'Cerrar sesión',
            'modules': 'MÓDULOS',
            'systemOnline': 'Sistema en Línea',
            'quickHelp': 'Ayuda Rápida',
            'todayTasks': 'Tareas de Hoy',
            'performance': 'Rendimiento',
            'pendingActions': 'Acciones Pendientes',
            'activeUsers': 'Usuarios Activos',
            'loading': 'Cargando...',
            'theme': 'Color del Tema',
            'language': 'Idioma',
            'custom': 'Color Personalizado',
            'switchShops': 'Cambiar Tiendas',
            'availableShops': 'Tiendas Disponibles',
            'loadingShops': 'Cargando tiendas...',
            'noShopsAvailable': 'No hay tiendas disponibles',
            'refreshShops': 'Actualizar Tiendas',
            'role': 'Rol',
            'close': 'Cerrar',
            'default': 'Predeterminado'
          }
        };
        if (dict[targetLang] && dict[targetLang][text]) {
          return dict[targetLang][text];
        }
        return text;
      }
    ];

    for (const api of apis) {
      const result = await api();
      if (result) return result;
    }
    return text;
  }

  async translateText(text, targetLang) {
    if (targetLang === 'en') return text;
    const cacheKey = `${text}_${targetLang}`;
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }
    const translated = await this.translateAPI(text, targetLang);
    this.cache.set(cacheKey, translated);
    this.saveToCache();
    return translated;
  }

  defaultTexts = {
    dashboard: 'Dashboard',
    welcome: 'Welcome',
    searchPlaceholder: 'Search modules, reports, or help...',
    notifications: 'Notifications',
    markAllRead: 'Mark all as read',
    viewAll: 'View All',
    myProfile: 'My Profile',
    settings: 'Settings',
    helpSupport: 'Help & Support',
    logout: 'Logout',
    modules: 'MODULES',
    systemOnline: 'System Online',
    quickHelp: 'Quick Help',
    todayTasks: "Today's Tasks",
    performance: 'Performance',
    pendingActions: 'Pending Actions',
    activeUsers: 'Active Users',
    loading: 'Loading...',
    theme: 'Theme Color',
    language: 'Language',
    custom: 'Custom Color',
    switchShops: 'Switch Shops',
    availableShops: 'Available Shops',
    loadingShops: 'Loading shops...',
    noShopsAvailable: 'No shops available',
    refreshShops: 'Refresh Shops',
    role: 'Role',
    close: 'Close',
    default: 'Default',
    current: 'Current'
  };

  async getTranslations(lang) {
    if (lang === 'en') return this.defaultTexts;
    const translations = {};
    const promises = [];
    Object.entries(this.defaultTexts).forEach(([key, text]) => {
      const cacheKey = `${text}_${lang}`;
      if (this.cache.has(cacheKey)) {
        translations[key] = this.cache.get(cacheKey);
      } else {
        promises.push(
          this.translateText(text, lang).then(translated => {
            translations[key] = translated;
          })
        );
      }
    });
    if (promises.length > 0) {
      await Promise.all(promises);
      this.saveToCache();
    }
    return translations;
  }
}

const translator = new InstantTranslator();

const LANGUAGES = {
  en: { name: 'English', icon: '🇺🇸', dir: 'ltr' },
  ur: { name: 'اردو', icon: '🇵🇰', dir: 'rtl' },
  ar: { name: 'عربي', icon: '🇸🇦', dir: 'rtl' },
  es: { name: 'Español', icon: '🇪🇸', dir: 'ltr' }
};

const THEME_COLORS = [
  { name: 'Indigo', value: '#6366f1' },
  { name: 'Purple', value: '#8b5cf6' },
  { name: 'Rose', value: '#f43f5e' },
  { name: 'Emerald', value: '#10b981' },
  { name: 'Amber', value: '#f59e0b' },
  { name: 'Cyan', value: '#06b6d4' },
  { name: 'Sky', value: '#0ea5e9' },
  { name: 'Fuchsia', value: '#d946ef' }
];

// ==============================================
// MAIN UNIFIED DASHBOARD COMPONENT
// ==============================================

const DashboardBusinessManager = () => {
  const { user, logout, setUser, currentShop } = useAuth();
  const navigate = useNavigate();

  // Get module configuration based on user role
  const moduleConfig = getModuleByRole(user?.role);
  const [moduleId, setModuleId] = useState(moduleConfig?.moduleId || 'default');

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [activeSubmenu, setActiveSubmenu] = useState('');
  const [formContent, setFormContent] = useState(null);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchResults, setSearchResults] = useState([]);

  // UI States
  const [darkMode, setDarkMode] = useState(() => {
    return localStorage.getItem('ourzest_darkMode') === 'true';
  });
  const [currentLanguage, setCurrentLanguage] = useState(() => {
    const saved = localStorage.getItem('ourzest_language');
    return LANGUAGES[saved] ? saved : 'en';
  });
  const [currentTheme, setCurrentTheme] = useState(() => {
    const saved = localStorage.getItem('ourzest_theme');
    return saved && /^#[0-9A-F]{6}$/i.test(saved) ? saved : '#6366f1';
  });
  const [translations, setTranslations] = useState({});
  const [translatedMenus, setTranslatedMenus] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showShortcuts, setShowShortcuts] = useState(false);
  const [refreshMessage, setRefreshMessage] = useState(null);
  const [translatedBusinessName, setTranslatedBusinessName] = useState('');

  // Side panel state
  const [activeSidePanel, setActiveSidePanel] = useState(null);
  const [sidePanelTitle, setSidePanelTitle] = useState('');
  const [sidePanelComponent, setSidePanelComponent] = useState(null);

  // Shop Switcher State
  const [showShopSwitcher, setShowShopSwitcher] = useState(false);
  const [userShops, setUserShops] = useState([]);
  const [loadingShops, setLoadingShops] = useState(false);
  const [hasMultipleShops, setHasMultipleShops] = useState(false);
  const [userRole, setUserRole] = useState('');

  // Notification State
  const [realNotifications, setRealNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showNotificationForm, setShowNotificationForm] = useState(false);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showThemePicker, setShowThemePicker] = useState(false);
  const [showLanguagePicker, setShowLanguagePicker] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [pollingInterval, setPollingInterval] = useState(null);
  const [loadingNotifications, setLoadingNotifications] = useState(false);

  // Shortcut Modal State
  const [shortcutModal, setShortcutModal] = useState({
    isOpen: false,
    title: '',
    component: null
  });

  // Refs
  const mainContentRef = useRef(null);
  const shortcutsRef = useRef(null);
  const notificationRef = useRef(null);
  const userMenuRef = useRef(null);
  const themePickerRef = useRef(null);
  const languagePickerRef = useRef(null);
  const searchRef = useRef(null);
  const shopSwitcherRef = useRef(null);
  const sidebarRef = useRef(null);

  // Menu groups state
  const [menuGroups, setMenuGroups] = useState([]);

  // ==============================================
  // GET ICON HELPER
  // ==============================================
  const getIcon = (iconName) => {
    if (!iconName) return Icons.faBars;
    if (typeof iconName === 'string') {
      const iconKey = Object.keys(Icons).find(key => 
        key.toLowerCase() === iconName.toLowerCase() ||
        key.toLowerCase() === `fa${iconName}`.toLowerCase()
      );
      if (iconKey) return Icons[iconKey];
      const prefixedKey = Object.keys(Icons).find(key => 
        key.toLowerCase() === `fa${iconName}`.toLowerCase()
      );
      if (prefixedKey) return Icons[prefixedKey];
      return Icons.faBars;
    }
    return iconName || Icons.faBars;
  };

  // ==============================================
  // FIX: PREVENT DUPLICATE SIDEBAR
  // ==============================================
  useEffect(() => {
    const existingSidebars = document.querySelectorAll('.sidebar');
    if (existingSidebars.length > 1) {
      existingSidebars.forEach((sidebar, index) => {
        if (index > 0) {
          sidebar.style.display = 'none';
        }
      });
      console.log('Duplicate sidebar detected - removed extra');
    }
  }, []);

  // ==============================================
  // INITIALIZATION
  // ==============================================

  useEffect(() => {
    const initialize = async () => {
      try {
        console.log('Initializing DashboardBusinessManager...');
        console.log('User role:', user?.role);
        console.log('Module config:', moduleConfig);
        
        applyTheme(currentTheme);
        await handleLanguageChange(currentLanguage, false);
        
        if (darkMode) {
          document.body.classList.add('dark-mode');
        }

        const trans = await translator.getTranslations(currentLanguage);
        setTranslations(trans);

        loadMenuGroups();

        const DashboardComponent = moduleConfig?.dashboardComponent || getDefaultDashboard();
        setFormContent(<DashboardComponent 
          onNavigate={handleMenuNavigation}
          setFormContent={setFormContent}
          onBreadcrumbUpdate={updateBreadcrumbs}
        />);

        setLoading(false);
        console.log('Dashboard initialized successfully');
      } catch (error) {
        console.error('Error initializing dashboard:', error);
        setLoading(false);
      }
    };

    initialize();

    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (mobile) {
        setSidebarOpen(false);
      } else {
        setSidebarOpen(true);
      }
    };

    window.addEventListener('resize', handleResize);
    handleResize();
    window.addEventListener('keydown', handleKeyboardShortcuts);

    if (user?.id) {
      loadUserShops();
    }

    if (user?.username) {
      fetchNotifications();
      const interval = setInterval(fetchNotifications, 21600000);
      setPollingInterval(interval);
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('keydown', handleKeyboardShortcuts);
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  useEffect(() => {
    if (user?.role) {
      const newConfig = getModuleByRole(user.role);
      if (newConfig) {
        setModuleId(newConfig.moduleId);
        loadTranslationsAndMenus();
      }
    }
  }, [user?.role]);

  useEffect(() => {
    if (currentLanguage) {
      loadTranslationsAndMenus();
    }
  }, [currentLanguage]);

  // ==============================================
  // LANGUAGE AND THEME HANDLERS
  // ==============================================

  const applyTheme = (color) => {
    if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
      color = '#6366f1';
    }

    localStorage.setItem('ourzest_theme', color);
    setCurrentTheme(color);

    const hex = color.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const adjust = (amount) => {
      let newR = Math.max(0, Math.min(255, r + amount));
      let newG = Math.max(0, Math.min(255, g + amount));
      let newB = Math.max(0, Math.min(255, b + amount));
      return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
    };

    document.documentElement.style.setProperty('--primary-color', color);
    document.documentElement.style.setProperty('--primary-dark', adjust(-40));
    document.documentElement.style.setProperty('--primary-light', adjust(40));
    document.documentElement.style.setProperty('--primary-shadow', `rgba(${r}, ${g}, ${b}, 0.1)`);
    document.documentElement.style.setProperty('--primary-color-rgb', `${r}, ${g}, ${b}`);
  };

  const handleLanguageChange = async (langCode, saveToStorage = true) => {
    if (!LANGUAGES[langCode]) {
      langCode = 'en';
    }

    if (saveToStorage) {
      localStorage.setItem('ourzest_language', langCode);
    }

    setCurrentLanguage(langCode);

    const langData = LANGUAGES[langCode];
    document.documentElement.dir = langData.dir;
    document.documentElement.lang = langCode;
    document.body.dir = langData.dir;

    const trans = await translator.getTranslations(langCode);
    setTranslations(trans);

    const originalName = currentShop?.shop_name || user?.businessname || user?.shopname || 'Welcome!';
    if (langCode === 'en') {
      setTranslatedBusinessName(originalName);
    } else {
      try {
        const translated = await translator.translateText(originalName, langCode);
        setTranslatedBusinessName(translated);
      } catch (error) {
        setTranslatedBusinessName(originalName);
      }
    }
  };

  const handleThemeChange = (color) => {
    applyTheme(color);
    setShowThemePicker(false);
  };

  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    localStorage.setItem('ourzest_darkMode', newDarkMode.toString());
    document.body.classList.toggle('dark-mode', newDarkMode);
  };

  // ==============================================
  // MENU AND PERMISSION HANDLERS - ROLE BASED
  // ==============================================

  const getAccessibleMenus = () => {
    if (!user || !moduleConfig) return [];

    const userRole = user.role;
    const menus = moduleConfig.menus || [];

    return menus.filter(menu => {
      const hasAccess = menu.roles.includes('all') || 
                        menu.roles.includes(userRole) || 
                        userRole === 'super_admin';
      
      if (hasAccess && menu.submenus) {
        menu.submenus = menu.submenus.filter(sub => 
          sub.roles.includes('all') || 
          sub.roles.includes(userRole) || 
          userRole === 'super_admin'
        );
      }
      
      return hasAccess;
    });
  };

  const loadTranslationsAndMenus = async () => {
    try {
      const trans = await translator.getTranslations(currentLanguage);
      setTranslations(trans);

      const accessibleMenus = getAccessibleMenus();
      const translated = accessibleMenus.map(menu => ({
        ...menu,
        translatedName: trans[menu.id] || menu.label,
        submenus: menu.submenus?.map(sub => ({
          ...sub,
          translatedName: trans[sub.id] || sub.label
        })) || []
      }));
      setTranslatedMenus(translated);
    } catch (error) {
      console.error('Error loading translations and menus:', error);
    }
  };

  const loadMenuGroups = () => {
    try {
      const saved = localStorage.getItem('dashboard_menu_groups');
      if (saved) {
        const parsed = JSON.parse(saved);
        setMenuGroups(parsed);
        return;
      }
    } catch (e) {
      console.error('Error loading menu groups:', e);
    }
    
    const defaultGroups = moduleConfig?.menuGroups || [
      { id: 'management', name: 'Management', icon: 'faUsersCog', order: 1 },
      { id: 'inventory', name: 'Inventory & Stock', icon: 'faBoxes', order: 2 },
      { id: 'sales', name: 'Sales & Transactions', icon: 'faMoneyBillWave', order: 3 },
      { id: 'system', name: 'System Settings', icon: 'faCog', order: 4 }
    ];
    setMenuGroups(defaultGroups);
  };

  const getGroupedMenus = (menus, groups) => {
    const grouped = {};
    
    groups.forEach(group => {
      grouped[group.id] = { ...group, menus: [] };
    });

    grouped['other'] = { id: 'other', name: 'Other', icon: 'faBars', menus: [] };

    menus.forEach(menu => {
      const groupId = menu.groupId || 'other';
      if (grouped[groupId]) {
        grouped[groupId].menus.push(menu);
      } else {
        grouped['other'].menus.push(menu);
      }
    });

    return Object.values(grouped)
      .filter(group => group.menus.length > 0)
      .sort((a, b) => (a.order || 999) - (b.order || 999));
  };

  // ==============================================
  // NAVIGATION HANDLERS
  // ==============================================

  const handleMenuClick = (menuId) => {
    if (activeMenu === menuId) {
      setActiveMenu('');
      setActiveSubmenu('');
      setFormContent(null);
      updateBreadcrumbs('', '');
      return;
    }

    setActiveMenu(menuId);
    setActiveSubmenu('');
    updateBreadcrumbs(menuId, '');

    const menu = translatedMenus.find(m => m.id === menuId);
    if (menu) {
      if (menuId === 'dashboard') {
        const DashboardComponent = moduleConfig?.dashboardComponent || getDefaultDashboard();
        setFormContent(<DashboardComponent 
          onNavigate={handleMenuNavigation}
          setFormContent={setFormContent}
          onBreadcrumbUpdate={updateBreadcrumbs}
        />);
      } else if (menu.component) {
        setFormContent(React.createElement(menu.component));
      } else if (menu.submenus && menu.submenus.length > 0) {
        const firstSubmenu = menu.submenus[0];
        setActiveSubmenu(firstSubmenu.id);
        updateBreadcrumbs(menuId, firstSubmenu.id);
        if (firstSubmenu.component) {
          setFormContent(React.createElement(firstSubmenu.component));
        }
      }
    }

    if (isMobile) {
      setSidebarOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  const handleSubmenuClick = (submenuId) => {
    setActiveSubmenu(submenuId);
    updateBreadcrumbs(activeMenu, submenuId);

    const menu = translatedMenus.find(m => m.id === activeMenu);
    if (menu) {
      const submenu = menu.submenus.find(s => s.id === submenuId);
      if (submenu?.component) {
        setFormContent(React.createElement(submenu.component));
      }
    }

    if (isMobile) {
      setSidebarOpen(false);
      document.body.style.overflow = 'auto';
    }
  };

  const handleMenuNavigation = (menuId, submenuId = null) => {
    if (submenuId) {
      setActiveMenu(menuId);
      setActiveSubmenu(submenuId);
      updateBreadcrumbs(menuId, submenuId);
      
      const menu = translatedMenus.find(m => m.id === menuId);
      if (menu) {
        const submenu = menu.submenus.find(s => s.id === submenuId);
        if (submenu?.component) {
          setFormContent(React.createElement(submenu.component));
        }
      }
    } else {
      handleMenuClick(menuId);
    }
  };

  const updateBreadcrumbs = (menuId, submenuId = null) => {
    const items = [
      { id: 'dashboard', label: translations.dashboard || 'Dashboard', isClickable: true }
    ];
    
    if (menuId && menuId !== 'dashboard') {
      const menu = translatedMenus.find(m => m.id === menuId);
      if (menu) {
        items.push({
          id: menuId,
          label: menu.translatedName || menu.label,
          isClickable: true
        });
      }
    }
    
    if (submenuId) {
      const menu = translatedMenus.find(m => m.id === menuId);
      if (menu) {
        const submenu = menu.submenus.find(s => s.id === submenuId);
        if (submenu) {
          items.push({
            id: submenuId,
            label: submenu.translatedName || submenu.label,
            isClickable: false
          });
        }
      }
    }
    
    const isRTL = currentLanguage === 'ar' || currentLanguage === 'ur';
    setBreadcrumbItems(isRTL && items.length > 1 ? [...items].reverse() : items);
  };

  const handleBreadcrumbClick = (item) => {
    if (item.isClickable) {
      if (item.id === 'dashboard') {
        handleMenuClick('dashboard');
      } else if (item.id === activeMenu) {
        setActiveSubmenu('');
        updateBreadcrumbs(activeMenu, '');
        const menu = translatedMenus.find(m => m.id === activeMenu);
        if (menu?.component) {
          setFormContent(React.createElement(menu.component));
        }
      } else {
        handleMenuNavigation(item.id);
      }
    }
  };

  // ==============================================
  // KEYBOARD SHORTCUTS
  // ==============================================

  const handleKeyboardShortcuts = (event) => {
    if ((event.ctrlKey || event.metaKey) && event.key === 'r') {
      event.preventDefault();
      refreshDashboard();
    }
    
    if (event.altKey && event.key === 'h') {
      event.preventDefault();
      setShowShortcuts(!showShortcuts);
    }
    
    if (event.key === 'Escape') {
      setShowShortcuts(false);
      setActiveSidePanel(null);
      setShowNotifications(false);
      setShowUserMenu(false);
      setShowThemePicker(false);
      setShowLanguagePicker(false);
      setShowShopSwitcher(false);
      if (shortcutModal.isOpen) {
        closeShortcutModal();
      }
    }

    if (event.altKey && event.key === 'd') {
      event.preventDefault();
      handleMenuClick('dashboard');
    }

    const moduleShortcuts = moduleConfig?.shortcuts || [];
    moduleShortcuts.forEach(shortcut => {
      const keyMatch = shortcut.key.match(/Alt\s*\+\s*([A-Z])/i);
      if (keyMatch && event.altKey && event.key.toLowerCase() === keyMatch[1].toLowerCase()) {
        event.preventDefault();
        const menuItem = translatedMenus.find(m => 
          m.translatedName?.toLowerCase().includes(shortcut.description?.toLowerCase().split(' ')[0] || '')
        );
        if (menuItem) {
          handleMenuClick(menuItem.id);
        }
      }
    });
  };

  const refreshDashboard = () => {
    if (activeMenu === 'dashboard') {
      const DashboardComponent = moduleConfig?.dashboardComponent || getDefaultDashboard();
      setFormContent(<DashboardComponent 
        key={Date.now()} 
        onNavigate={handleMenuNavigation}
        setFormContent={setFormContent}
        onBreadcrumbUpdate={updateBreadcrumbs}
      />);
    } else if (activeSubmenu) {
      const menu = translatedMenus.find(m => m.id === activeMenu);
      if (menu) {
        const submenu = menu.submenus.find(s => s.id === activeSubmenu);
        if (submenu?.component) {
          setFormContent(React.createElement(submenu.component, { key: Date.now() }));
        }
      }
    } else {
      const menu = translatedMenus.find(m => m.id === activeMenu);
      if (menu?.component) {
        setFormContent(React.createElement(menu.component, { key: Date.now() }));
      }
    }
    
    showRefreshNotification('Dashboard refreshed successfully!');
  };

  // ==============================================
  // UI HELPERS
  // ==============================================

  const getDefaultDashboard = () => {
    return () => (
      <div className="default-dashboard">
        <div className="welcome-section">
          <h1>Welcome to {moduleConfig?.moduleName || 'Dashboard'}</h1>
          <p>{moduleConfig?.description || 'Select a menu to get started'}</p>
        </div>
        {moduleConfig?.quickStats && (
          <div className="quick-stats">
            {moduleConfig.quickStats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={getIcon(stat.icon)} />
                </div>
                <div className="stat-content">
                  <h3>{stat.value}</h3>
                  <p>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  const showRefreshNotification = (message) => {
    setRefreshMessage(message);
    setTimeout(() => setRefreshMessage(null), 3000);
  };

  const getShortcuts = () => {
    const baseShortcuts = [
      { key: 'Alt + D', description: 'Dashboard' },
      { key: 'Ctrl + R / Cmd + R', description: 'Refresh Dashboard Data' },
      { key: 'Alt + H', description: 'Toggle Shortcuts Panel' },
      { key: 'Esc', description: 'Close panels / modals' }
    ];
    
    const moduleShortcuts = moduleConfig?.shortcuts || [];
    return [...baseShortcuts, ...moduleShortcuts];
  };

  // ==============================================
  // SHOP SWITCHER FUNCTIONS
  // ==============================================

  const loadUserShops = async () => {
    if (!user?.id) return;
    setLoadingShops(true);
    try {
      const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'get_user_shop_roles',
          user_id: user.id
        })
      });
      const data = await response.json();
      if (data.success && data.data) {
        setUserShops(data.data);
        setHasMultipleShops(data.data.length > 1);
        if (data.data.length > 0) {
          const current = data.data.find(s => s.shop_id === currentShop?.shop_id);
          setUserRole(current?.role || user.role);
        }
      }
    } catch (error) {
      console.error('Error loading shops:', error);
    } finally {
      setLoadingShops(false);
    }
  };

  const handleShopSwitch = async (shop) => {
    if (shop.shop_id === currentShop?.shop_id) {
      setShowShopSwitcher(false);
      return;
    }
    try {
      const updatedUser = {
        ...user,
        shop_id: shop.shop_id,
        shop_name: shop.shop_name,
        shop_code: shop.shop_code,
        role: shop.role || user.role
      };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      localStorage.setItem('user_role', shop.role || user.role);
      localStorage.setItem('current_shop', JSON.stringify({
        shop_id: shop.shop_id,
        shop_name: shop.shop_name,
        shop_code: shop.shop_code
      }));
      setShowShopSwitcher(false);
      setTimeout(() => window.location.reload(), 300);
    } catch (error) {
      console.error('Error switching shop:', error);
      alert('Error switching shop. Please try again.');
    }
  };

  // ==============================================
  // NOTIFICATION FUNCTIONS
  // ==============================================

  const fetchNotifications = async () => {
    if (!user?.username) return;
    try {
      setLoadingNotifications(true);
      const authData = {
        user_id: user.id,
        username: user.username,
        role: user.role
      };
      const authToken = btoa(JSON.stringify(authData));
      const response = await fetch(`https://ourzestapp.online/oz_backend/notifications.php?action=list&limit=20`, {
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      if (data.success) {
        const sorted = data.notifications.sort((a, b) => {
          if (a.is_read !== b.is_read) return a.is_read ? 1 : -1;
          return new Date(b.created_at) - new Date(a.created_at);
        });
        setRealNotifications(sorted);
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoadingNotifications(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      const authData = {
        user_id: user.id,
        username: user.username,
        role: user.role
      };
      const authToken = btoa(JSON.stringify(authData));
      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'mark_read', id })
      });
      const data = await response.json();
      if (data.success) {
        setRealNotifications(prev =>
          prev.map(n => n.id === id ? { ...n, is_read: true } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const authData = {
        user_id: user.id,
        username: user.username,
        role: user.role
      };
      const authToken = btoa(JSON.stringify(authData));
      const response = await fetch('https://ourzestapp.online/oz_backend/notifications.php', {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${authToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ action: 'mark_all_read' })
      });
      const data = await response.json();
      if (data.success) {
        setRealNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // ==============================================
  // SHORTCUT MODAL FUNCTIONS
  // ==============================================

  const openShortcutModal = (title, component) => {
    setShortcutModal({ isOpen: true, title, component });
    setShowShortcuts(false);
  };

  const closeShortcutModal = () => {
    setShortcutModal({ isOpen: false, title: '', component: null });
  };

  // ==============================================
  // SIDE PANEL FUNCTIONS
  // ==============================================

  const toggleSidePanel = (panelId, title, component) => {
    if (activeSidePanel === panelId) {
      setActiveSidePanel(null);
      setSidePanelTitle('');
      setSidePanelComponent(null);
    } else {
      setActiveSidePanel(panelId);
      setSidePanelTitle(title);
      setSidePanelComponent(component);
    }
  };

  // ==============================================
  // LOGOUT
  // ==============================================

  const handleLogout = () => {
    localStorage.removeItem('ourzest_darkMode');
    logout();
    navigate('/login');
  };

  // ==============================================
  // TOGGLE FUNCTIONS
  // ==============================================

  const toggleSidebar = () => {
    const newState = !sidebarOpen;
    setSidebarOpen(newState);
    if (isMobile) {
      document.body.style.overflow = newState ? 'hidden' : 'auto';
    }
  };

  const toggleShortcuts = () => {
    setShowShortcuts(!showShortcuts);
  };

  const toggleFullscreen = () => {
    if (!fullscreen) {
      document.documentElement.requestFullscreen?.() || 
      document.documentElement.webkitRequestFullscreen?.() ||
      document.documentElement.msRequestFullscreen?.();
      setFullscreen(true);
    } else {
      document.exitFullscreen?.() || 
      document.webkitExitFullscreen?.() ||
      document.msExitFullscreen?.();
      setFullscreen(false);
    }
  };

  const closeAllDropdowns = () => {
    setShowNotifications(false);
    setShowUserMenu(false);
    setShowThemePicker(false);
    setShowLanguagePicker(false);
    setShowShopSwitcher(false);
    if (isMobile) {
      document.body.style.overflow = 'auto';
    }
  };

  // ==============================================
  // RENDER - IMPROVED LOADING SCREEN
  // ==============================================

  if (loading) {
    return (
      <div className="dashboard-loading-screen">
        <div className="loading-content-center">
          <div className="loading-spinner-modern">
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
            <div className="spinner-ring"></div>
          </div>
          <div className="loading-text">
            <h2>{moduleConfig?.moduleName || 'Loading'}</h2>
            <p>Preparing your workspace...</p>
          </div>
          <div className="loading-progress">
            <div className="progress-bar">
              <div className="progress-fill"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!moduleConfig) {
    return (
      <div className="dashboard-error-screen">
        <div className="error-content">
          <div className="error-icon">⚠️</div>
          <h2>Module not configured</h2>
          <p>Your role ({user?.role}) does not have a configured module.</p>
          <button className="error-btn" onClick={() => navigate('/login')}>Return to Login</button>
        </div>
      </div>
    );
  }

  const shortcuts = getShortcuts();
  const groupedMenus = getGroupedMenus(translatedMenus, menuGroups);

  return (
    <div className={`dashboard-business-manager ${darkMode ? 'dark-mode' : ''} 
      ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'} 
      ${LANGUAGES[currentLanguage]?.dir || 'ltr'}`}
    >
      {/* Top Navigation Bar */}
      <nav className="top-navbar">
        <div className="nav-left">
          <button className="sidebar-toggle" onClick={toggleSidebar} aria-label="Toggle sidebar">
            <FontAwesomeIcon icon={Icons.faBars} />
          </button>
          <div className="animated-business-name">
            <span className="typewriter-text" dir="auto">
              {translatedBusinessName || currentShop?.shop_name || user?.businessname || user?.shopname || 'Welcome!'}
            </span>
            <span className="typewriter-cursor"></span>
          </div>
        </div>

        {/* Breadcrumb */}
        <div className="nav-center">
          <div className="breadcrumb-container">
            {breadcrumbItems.length > 0 && (
              <div className="breadcrumb-wrapper">
                {breadcrumbItems.map((item, index) => (
                  <React.Fragment key={item.id}>
                    {index > 0 && (
                      <span className="breadcrumb-separator">
                        <FontAwesomeIcon icon={Icons.faChevronRight} />
                      </span>
                    )}
                    <span
                      className={`breadcrumb-item ${item.isClickable ? 'clickable' : ''}`}
                      onClick={() => item.isClickable && handleBreadcrumbClick(item)}
                      style={{
                        color: item.isClickable ? 'var(--primary-color)' : 'var(--text-secondary)',
                        cursor: item.isClickable ? 'pointer' : 'default'
                      }}
                    >
                      {item.label}
                    </span>
                  </React.Fragment>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="nav-right">
          {/* Shortcuts Toggle - Desktop only */}
          <button
            className={`nav-btn shortcuts-toggle ${showShortcuts ? 'active' : ''}`}
            onClick={toggleShortcuts}
            title="Keyboard Shortcuts (Alt+H)"
          >
            <FontAwesomeIcon icon={Icons.faKeyboard} />
          </button>

          {/* Fullscreen Toggle - Desktop only */}
          <button className="nav-btn" onClick={toggleFullscreen} title={fullscreen ? 'Exit Fullscreen' : 'Fullscreen'}>
            <FontAwesomeIcon icon={fullscreen ? Icons.faCompress : Icons.faExpand} />
          </button>

          {/* Dark Mode Toggle */}
          <button className="nav-btn" onClick={toggleDarkMode} title={darkMode ? 'Light Mode' : 'Dark Mode'}>
            <FontAwesomeIcon icon={darkMode ? Icons.faSun : Icons.faMoon} />
          </button>

          {/* Theme Picker */}
          <div className="theme-picker-container" ref={themePickerRef}>
            <button className="nav-btn" onClick={() => setShowThemePicker(!showThemePicker)} title="Theme Color">
              <FontAwesomeIcon icon={Icons.faPalette} />
            </button>
            {showThemePicker && (
              <div className="theme-picker-dropdown">
                <div className="theme-picker-header">
                  <h4>{translations.theme || 'Theme Color'}</h4>
                </div>
                <div className="theme-presets-grid">
                  {THEME_COLORS.map((color) => (
                    <button
                      key={color.value}
                      className={`theme-preset ${currentTheme === color.value ? 'active' : ''}`}
                      style={{ backgroundColor: color.value }}
                      onClick={() => handleThemeChange(color.value)}
                      title={color.name}
                    />
                  ))}
                </div>
                <div className="custom-theme-input">
                  <input type="color" value={currentTheme} onChange={(e) => handleThemeChange(e.target.value)} />
                  <span>{translations.custom || 'Custom Color'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Language Picker */}
          <div className="language-picker-container" ref={languagePickerRef}>
            <button className="nav-btn" onClick={() => setShowLanguagePicker(!showLanguagePicker)} title={translations.language || 'Language'}>
              <FontAwesomeIcon icon={Icons.faLanguage} />
              <span className="current-lang-flag">{LANGUAGES[currentLanguage]?.icon}</span>
            </button>
            {showLanguagePicker && (
              <div className="language-dropdown">
                <div className="language-picker-header">
                  <h4>{translations.language || 'Language'}</h4>
                </div>
                <div className="language-options">
                  {Object.entries(LANGUAGES).map(([key, lang]) => (
                    <div
                      key={key}
                      className={`language-option ${currentLanguage === key ? 'active' : ''}`}
                      onClick={() => handleLanguageChange(key)}
                    >
                      <span className="lang-flag">{lang.icon}</span>
                      <span className="lang-name">{lang.name}</span>
                      {currentLanguage === key && <FontAwesomeIcon icon={Icons.faCheckCircle} className="check-icon" />}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="notification-container" ref={notificationRef}>
            <button
              className="nav-btn notification-btn"
              onClick={() => {
                setShowNotifications(false);
                setShowUserMenu(false);
                setShowThemePicker(false);
                setShowLanguagePicker(false);
                setShowShopSwitcher(false);
                setActiveMenu('notifications');
                setActiveSubmenu('');
                updateBreadcrumbs('notifications', '');
                setFormContent(<MessageSystem />);
                if (isMobile) {
                  setSidebarOpen(false);
                  document.body.style.overflow = 'auto';
                }
              }}
              title="Notifications"
            >
              <FontAwesomeIcon icon={Icons.faBell} />
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>
          </div>

          {/* Shop Switcher */}
          {hasMultipleShops && user?.role !== 'customer' && (
            <div className="shop-switcher-wrapper" ref={shopSwitcherRef}>
              <button
                className="shop-switcher-btn"
                onClick={() => {
                  if (!showShopSwitcher) loadUserShops();
                  setShowShopSwitcher(!showShopSwitcher);
                }}
                title={`${translations.switchShops || 'Switch Shops'} (${userShops.length} ${translations.availableShops || 'available'})`}
              >
                {loadingShops ? (
                  <FontAwesomeIcon icon={Icons.faSpinner} className="shop-icon loading" spin />
                ) : (
                  <>
                    <FontAwesomeIcon icon={Icons.faStore} className="shop-icon" />
                    <FontAwesomeIcon icon={Icons.faExchangeAlt} className="switch-icon" />
                  </>
                )}
                {userShops.length > 0 && <span className="shop-count-badge">{userShops.length}</span>}
              </button>

              {showShopSwitcher && (
                <>
                  <div className="shop-switcher-overlay" onClick={() => setShowShopSwitcher(false)} />
                  <div className="shop-switcher-dropdown">
                    <div className="shop-switcher-header">
                      <h4>{translations.switchShops || 'Switch Shops'}</h4>
                      {isMobile && (
                        <button className="close-dropdown-btn" onClick={() => setShowShopSwitcher(false)}>
                          <FontAwesomeIcon icon={Icons.faTimes} />
                        </button>
                      )}
                    </div>
                    {loadingShops ? (
                      <div className="shop-switcher-loading">
                        <FontAwesomeIcon icon={Icons.faSpinner} spin />
                        <p>{translations.loadingShops || 'Loading shops...'}</p>
                      </div>
                    ) : userShops.length === 0 ? (
                      <div className="no-shops-message">
                        <FontAwesomeIcon icon={Icons.faStore} />
                        <p>{translations.noShopsAvailable || 'No shops available'}</p>
                        <button className="refresh-shops-btn" onClick={loadUserShops}>
                          <FontAwesomeIcon icon={Icons.faRefresh} /> {translations.refreshShops || 'Refresh Shops'}
                        </button>
                      </div>
                    ) : (
                      <div className="shop-switcher-list">
                        {userShops.map((shop) => (
                          <div
                            key={shop.shop_id}
                            className={`shop-switcher-item ${shop.shop_id === currentShop?.shop_id ? 'active' : ''}`}
                            onClick={() => handleShopSwitch(shop)}
                          >
                            <div className="shop-item-icon">
                              <FontAwesomeIcon icon={shop.shop_id === currentShop?.shop_id ? Icons.faHome : Icons.faStore} />
                            </div>
                            <div className="shop-item-details">
                              <div className="shop-item-header">
                                <h5 className="shop-name">{shop.shop_name}</h5>
                                {shop.role && <span className="shop-role-badge">{shop.role.replace('_', ' ')}</span>}
                              </div>
                              {shop.shop_code && <div className="shop-code">Code: {shop.shop_code}</div>}
                            </div>
                            {shop.shop_id === currentShop?.shop_id && (
                              <FontAwesomeIcon icon={Icons.faCheck} className="current-indicator" />
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="shop-switcher-footer">
                      <span>{translations.role || 'Role'}: <strong>{userRole || user?.role}</strong></span>
                      {!isMobile && (
                        <button className="close-switcher-btn" onClick={() => setShowShopSwitcher(false)}>
                          {translations.close || 'Close'}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* User Menu */}
          <div className="user-container" ref={userMenuRef}>
            <button className="user-btn" onClick={() => setShowUserMenu(!showUserMenu)}>
              <div className="user-avatar">
                {user?.fullname?.charAt(0) || user?.username?.charAt(0) || user?.businessname?.charAt(0) || 'U'}
              </div>
              <span className="user-name">{user?.fullname || user?.businessname || user?.username}</span>
            </button>

            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-info">
                  {isMobile && (
                    <button className="close-dropdown-btn" onClick={() => setShowUserMenu(false)}>
                      <FontAwesomeIcon icon={Icons.faTimes} />
                    </button>
                  )}
                  <div className="user-avatar-large">
                    {user?.fullname?.charAt(0) || user?.username?.charAt(0) || user?.businessname?.charAt(0) || 'U'}
                  </div>
                  <div className="user-details">
                    <h4>{user?.fullname || user?.businessname || user?.username}</h4>
                    <p className="user-role">
                      <FontAwesomeIcon icon={Icons.faIdCard} /> {user?.role?.toUpperCase()}
                    </p>
                    <p className="user-email"><FontAwesomeIcon icon={Icons.faEnvelope} /> {user?.email || 'No email'}</p>
                    {currentShop && user?.role !== 'customer' && (
                      <div className="current-shop-info">
                        <strong>{currentShop.shop_name || `Shop #${currentShop.shop_id}`}</strong>
                      </div>
                    )}
                  </div>
                </div>
                <div className="user-menu">
                  <Link to="/dashboard/help" className="menu-item" onClick={closeAllDropdowns}>
                    <FontAwesomeIcon icon={Icons.faQuestionCircle} />
                    <span>{translations.helpSupport || 'Help & Support'}</span>
                  </Link>
                  <div className="menu-divider"></div>
                  <button className="menu-item logout-btn" onClick={handleLogout}>
                    <FontAwesomeIcon icon={Icons.faSignOutAlt} />
                    <span>{translations.logout || 'Logout'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </nav>

      {/* Sidebar */}
      <aside className="sidebar" ref={sidebarRef}>
        <div className="sidebar-header">
          <h3>{moduleConfig?.moduleName || 'MODULES'}</h3>
          <div className="header-right">
            <span className="module-count">{translatedMenus.length}</span>
            {isMobile && (
              <button className="sidebar-close-mobile" onClick={toggleSidebar}>
                <FontAwesomeIcon icon={Icons.faTimes} />
              </button>
            )}
          </div>
        </div>
        <div className="sidebar-content">
          <div className="main-nav">
            {groupedMenus.map((group) => (
              <div key={group.id} className="menu-group">
                <div className="menu-group-header">
                  <FontAwesomeIcon icon={getIcon(group.icon)} className="group-icon" />
                  <span className="group-name">{group.name}</span>
                  <span className="group-count">{group.menus.length}</span>
                </div>

                <div className="menu-group-items">
                  {group.menus.map((menu) => (
                    <div key={menu.id} className="menu-wrapper" data-menu={menu.id}>
                      <div
                        className={`sidebar-item ${activeMenu === menu.id ? 'active' : ''}`}
                        onClick={() => handleMenuClick(menu.id)}
                      >
                        <div className="sidebar-icon">
                          <FontAwesomeIcon icon={getIcon(menu.icon)} />
                        </div>
                        <span className="sidebar-text">{menu.translatedName || menu.label}</span>
                        {menu.submenus && menu.submenus.length > 0 && (
                          <FontAwesomeIcon
                            icon={activeMenu === menu.id ? Icons.faChevronUp : Icons.faChevronDown}
                            className="dropdown-icon"
                          />
                        )}
                        {activeMenu === menu.id && <div className="active-indicator"></div>}
                      </div>

                      {activeMenu === menu.id && menu.submenus && menu.submenus.length > 0 && (
                        <div className="submenu-container">
                          {menu.submenus.map((submenu) => (
                            <div
                              key={submenu.id}
                              className={`submenu-item ${activeSubmenu === submenu.id ? 'active' : ''}`}
                              onClick={() => handleSubmenuClick(submenu.id)}
                            >
                              <FontAwesomeIcon icon={getIcon(submenu.icon)} className="submenu-icon" />
                              <span className="submenu-text">{submenu.translatedName || submenu.label}</span>
                              {activeSubmenu === submenu.id && <div className="submenu-indicator"></div>}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="sidebar-footer">
          <div className="system-status">
            <div className="status-indicator online"></div>
            <span>{translations.systemOnline || 'System Online'}</span>
          </div>
          <div className="quick-help">
            <button className="help-btn">
              <FontAwesomeIcon icon={Icons.faQuestionCircle} />
            </button>
          </div>
          {/* Mobile More Account */}
          <div className="mobile-more-account">
            <div className="mobile-more-account-identity">
              <div className="mobile-more-account-avatar">
                {user?.fullname?.charAt(0) || user?.username?.charAt(0) || user?.businessname?.charAt(0) || 'U'}
              </div>
              <div className="mobile-more-account-copy">
                <strong>{user?.fullname || user?.businessname || user?.username || 'Account'}</strong>
                <span>{user?.role?.replace(/_/g, ' ') || 'User'}</span>
              </div>
            </div>
            <button type="button" className="mobile-more-logout" onClick={handleLogout}>
              <FontAwesomeIcon icon={Icons.faSignOutAlt} />
              <span>{translations.logout || 'Logout'}</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`main-content ${activeSidePanel ? `side-panel-${activeSidePanel}` : ''}`} ref={mainContentRef}>
        <div className="page-content">
          <Suspense fallback={
            <div className="loading-content">
              <div className="spinner"></div>
              <p>{translations.loading || 'Loading...'}</p>
            </div>
          }>
            {formContent}
          </Suspense>
        </div>

        {moduleConfig?.quickStats && user?.role !== 'customer' && !user?.role?.includes('supplier') && (
          <div className="quick-stats">
            {moduleConfig.quickStats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-icon">
                  <FontAwesomeIcon icon={getIcon(stat.icon)} />
                </div>
                <div className="stat-content">
                  <h3>{stat.value}</h3>
                  <p>{stat.label}</p>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      {/* Refresh Toast */}
      {refreshMessage && (
        <div className="refresh-toast">
          <FontAwesomeIcon icon={Icons.faCheckCircle} />
          <span>{refreshMessage}</span>
        </div>
      )}

      {/* Dynamic Side Panel */}
      {activeSidePanel && sidePanelComponent && (
        <SidePanel
          isOpen={true}
          onClose={() => {
            setActiveSidePanel(null);
            setSidePanelTitle('');
            setSidePanelComponent(null);
          }}
          title={sidePanelTitle}
          initialWidth={500}
        >
          <Suspense fallback={
            <div className="loading-content">
              <div className="spinner"></div>
              <p>Loading...</p>
            </div>
          }>
            {sidePanelComponent}
          </Suspense>
        </SidePanel>
      )}

      {/* Keyboard Shortcuts Popup */}
      {showShortcuts && (
        <div className="keyboard-shortcuts-popup" ref={shortcutsRef}>
          <div className="shortcuts-header">
            <h4>
              <FontAwesomeIcon icon={Icons.faKeyboard} />
              Keyboard Shortcuts - {moduleConfig?.moduleName || 'Dashboard'}
            </h4>
            <button className="close-shortcuts" onClick={toggleShortcuts}>
              <FontAwesomeIcon icon={Icons.faTimes} />
            </button>
          </div>
          <div className="shortcuts-list">
            {shortcuts.map((shortcut, index) => (
              <div key={index} className="shortcut-item">
                <span className="shortcut-key">{shortcut.key}</span>
                <span className="shortcut-description">{shortcut.description}</span>
              </div>
            ))}
          </div>
          <div className="shortcuts-footer">
            <small>Press Alt+H to toggle this panel | Press Esc to close | Press Ctrl+R to refresh data</small>
          </div>
        </div>
      )}

      {/* Shortcut Modal */}
      {shortcutModal.isOpen && (
        <div className="shortcut-modal-overlay" onClick={closeShortcutModal}>
          <div className="shortcut-modal-container" onClick={(e) => e.stopPropagation()}>
            <div className="shortcut-modal-header">
              <div className="shortcut-modal-title">
                <FontAwesomeIcon icon={Icons.faKeyboard} className="modal-title-icon" />
                <h3>{shortcutModal.title}</h3>
              </div>
              <button className="shortcut-modal-close" onClick={closeShortcutModal}>
                <FontAwesomeIcon icon={Icons.faTimes} />
              </button>
            </div>
            <div className="shortcut-modal-content">
              <Suspense fallback={
                <div className="modal-loading">
                  <div className="spinner"></div>
                  <p>Loading...</p>
                </div>
              }>
                {shortcutModal.component}
              </Suspense>
            </div>
            <div className="shortcut-modal-footer">
              <button className="modal-footer-btn" onClick={closeShortcutModal}>
                <FontAwesomeIcon icon={Icons.faTimes} /> Close (Esc)
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Notification Form Modal */}
      {showNotificationForm && (
        <div className="modal-overlay" onClick={() => setShowNotificationForm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <NotificationForm onClose={() => setShowNotificationForm(false)} />
          </div>
        </div>
      )}

      {/* Dropdown Overlay */}
      {(showNotifications || showUserMenu || showThemePicker || showLanguagePicker || showShopSwitcher) && (
        <div className="dropdown-overlay" onClick={closeAllDropdowns} style={{ pointerEvents: 'auto' }} />
      )}
    </div>
  );
};

export default DashboardBusinessManager;